// Shared component primitives + the theme provider used across apps.
// Heavy lifting (modals, menus, dropdowns) is provided by Radix UI
// directly inside the apps that need them; this package only exposes
// the cn() helper, layout shells, and ThemeProvider/useTheme.
export { cn } from './cn';
export { ThemeProvider, useTheme, type ThemeChoice, type ThemeResolved } from './theme';
export { SurveyForm, letterFor, type SurveyFormProps } from './SurveyForm';
export { Markdown, renderMarkdown, type MarkdownProps } from './markdown';
export {
  HelpHint,
  FieldCaption,
  type HelpHintProps,
  type FieldCaptionProps,
} from './HelpHint';

import type { ReactNode } from 'react';
import { cn } from './cn';

/**
 * Helm wordmark / icon. The SVG lives in the shell's public dir so
 * it's served at the site root regardless of which app code-split
 * chunk is on screen.
 *
 * Pass `href` (typically `"#"` to clear the route hash and land
 * back on the landing page) to render as an anchor so users can
 * always click home from any role-specific chrome.
 */
export interface LogoProps {
  className?: string;
  alt?: string;
  /** When provided, the logo renders as an anchor with this href. */
  href?: string;
}

export function Logo({ className, alt = 'Helm', href }: LogoProps) {
  const img = (
    <img
      src="/helm-logo.svg"
      alt={alt}
      className={cn('h-8 w-auto select-none', className)}
      draggable={false}
    />
  );
  if (!href) return img;
  return (
    <a
      href={href}
      aria-label="Helm — back to home"
      className="inline-flex items-center rounded outline-none transition hover:opacity-80 focus-visible:ring-2 focus-visible:ring-primary"
    >
      {img}
    </a>
  );
}

export interface CardProps {
  /** String renders as the default h3; ReactNode lets a caller
   *  embed a back-button + state badge alongside the title without
   *  rewriting the header chrome. */
  title?: ReactNode;
  className?: string;
  actions?: ReactNode;
  children: ReactNode;
}

/** Card primitive used by every dashboard panel. */
export function Card({ title, actions, className, children }: CardProps) {
  return (
    <section
      className={cn(
        'rounded-md border border-border bg-card text-card-foreground shadow-sm',
        className,
      )}
    >
      {(title || actions) && (
        <header className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 border-b border-border px-4 py-3">
          {title ? (
            typeof title === 'string' ? (
              <h3 className="min-w-0 text-sm font-semibold">{title}</h3>
            ) : (
              <div className="min-w-0 text-sm font-semibold">{title}</div>
            )
          ) : (
            <span />
          )}
          {actions ? (
            <div className="flex min-w-0 flex-wrap items-center gap-2">{actions}</div>
          ) : null}
        </header>
      )}
      <div className="p-4">{children}</div>
    </section>
  );
}

export interface PanelProps {
  title?: string;
  children: ReactNode;
}

/** Legacy primitive used by Phase 8/12 placeholders. */
export function Panel({ title, children }: PanelProps) {
  return (
    <Card title={title}>
      <>{children}</>
    </Card>
  );
}
